import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { usePermission } from '../context/PermissionContext';
import { useSortable } from '../hooks/useTableUtils';
import { fmtListDate } from '../utils/date';

const statusBadge = { active:'badge-green', preparing:'badge-yellow', discontinued:'badge-gray' };

const TYPE_LABEL = { software:'Software', license:'License', hardware:'Hardware', appliance:'Appliance' };
const TYPE_SHORT = { software:'SW', license:'LIC', hardware:'HW', appliance:'AP' };
const TYPE_BADGE = { software:'badge-blue', license:'badge-purple', hardware:'badge-gray', appliance:'badge-green' };

export default function ProductListPage() {
  const navigate = useNavigate();
  const { canPerformAction } = usePermission();
  const canEdit = canPerformAction('제품 등록/편집');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priceSets, setPriceSets] = useState([]);
  const [selectedPriceSet, setSelectedPriceSet] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showDelete, setShowDelete] = useState(null);
  const [usage, setUsage] = useState(null); // 삭제 확인창: 이 상품을 사용 중인 견적서 수 / Appliance 구성품 사용 수
  // 삭제예정 상품: 삭제 요청 후 7일간 보관되며 이 기간에는 복원할 수 있음
  const [view, setView] = useState('list');            // 'list' | 'deleted'
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    api.get('/settings/codes/product_category').then(setCategories);
    api.get('/settings/codes/product_status').then(setStatuses);
    api.get('/pricesets').then(data => {
      setPriceSets(data);
      const active = data.find(s => s.is_active);
      if (active) setSelectedPriceSet(String(active.id));
    });
  }, []);

  const activeSetId = priceSets.find(s => s.is_active)?.id;
  const isComparing = selectedPriceSet && String(activeSetId) !== selectedPriceSet;
  const compareSetVersion = priceSets.find(s => String(s.id) === selectedPriceSet)?.version;

  const loadProducts = useCallback(async () => {
    setLoading(true);
    if (!selectedPriceSet) { setProducts([]); setLoading(false); return; }
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (catFilter) params.set('category', catFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);
      if (isComparing) params.set('price_set_id', selectedPriceSet);
      setProducts(await api.get(`/products?${params}`));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, catFilter, statusFilter, typeFilter, selectedPriceSet, isComparing]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const loadDeleted = useCallback(async () => {
    try { setDeletedProducts(await api.get('/products?deleted=1')); } catch (err) { console.error(err); }
  }, []);
  useEffect(() => { loadDeleted(); }, [loadDeleted]);

  // 정렬 (기본: 제품구분 오름차순)
  const { sorted: sortedProducts, toggleSort, SortIcon } = useSortable(products, 'category', 'asc');

  const handleClone = async (id) => {
    try { await api.post(`/products/${id}/clone`); loadProducts(); }
    catch (e) { alert(e.message); }
  };

  const openDelete = (p) => {
    setShowDelete(p); setDeletePassword(''); setUsage(null);
    api.get(`/products/${p.id}/usage`).then(setUsage).catch(() => {});
  };

  const handleDelete = async () => {
    if (!deletePassword) return;
    try {
      await api.del(`/products/${showDelete.id}`, { password: deletePassword });
      setShowDelete(null); setDeletePassword(''); loadProducts(); loadDeleted();
    } catch (e) { alert(e.message); }
  };

  const handleRestore = async (p) => {
    if (!window.confirm(`"${p.name} (${p.alias || p.model || p.code})"을(를) 복원하시겠습니까?`)) return;
    try { await api.post(`/products/${p.id}/restore`); loadDeleted(); loadProducts(); }
    catch (e) { alert(e.message); }
  };

  const tabBar = (
    <div className="tabs">
      <div className={`tab ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>상품 목록</div>
      <div className={`tab ${view === 'deleted' ? 'active' : ''}`} onClick={() => setView('deleted')}>
        삭제예정 상품{deletedProducts.length > 0 ? ` (${deletedProducts.length})` : ''}
      </div>
    </div>
  );

  if (view === 'deleted') {
    // deleted_at 은 '완전 삭제 예정 시각'(삭제 요청 + 7일) — 삭제 요청일은 7일 전
    const DAY = 86400000;
    const remainText = (p) => {
      const left = new Date(p.deleted_at).getTime() - Date.now();
      return left <= 0 ? { text: '기한 경과', color: '#dc2626' } : { text: `D-${Math.ceil(left / DAY)}`, color: '#b45309' };
    };
    return (
      <div>
        {tabBar}
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ padding:'10px 16px', background:'#fffbeb', borderBottom:'1px solid #fde68a', fontSize:12, color:'#92400e' }}>
            삭제를 요청한 상품은 7일간 여기에 보관되며, 이 기간에는 복원할 수 있습니다.
          </div>
          <div style={{ overflowX:'auto' }}>
            <table>
              <thead><tr>
                <th>상품코드</th><th>유형</th><th>상품구분</th><th>상품명</th><th>모델명</th><th>별칭</th>
                <th>삭제 요청일</th><th>완전삭제 예정일</th><th>남은 기간</th><th>삭제 요청자</th><th>작업</th>
              </tr></thead>
              <tbody>
                {deletedProducts.map(p => {
                  const remain = remainText(p);
                  return (
                    <tr key={p.id}>
                      <td style={{ fontFamily:'monospace' }}>{p.code}</td>
                      <td><span className={`badge ${TYPE_BADGE[p.product_type]||'badge-gray'}`} title={TYPE_LABEL[p.product_type]||p.product_type}>{TYPE_SHORT[p.product_type]||p.product_type||'-'}</span></td>
                      <td><span className="badge badge-gray">{p.category}</span></td>
                      <td className="fw-bold">{p.name}</td>
                      <td>{p.model}</td>
                      <td>{p.alias || '-'}</td>
                      <td>{fmtListDate(new Date(new Date(p.deleted_at).getTime() - 7 * DAY))}</td>
                      <td>{fmtListDate(p.deleted_at)}</td>
                      <td style={{ fontWeight:700, color:remain.color }}>{remain.text}</td>
                      <td>{p.deleted_by_name || '-'}</td>
                      <td><div className="btn-group">
                        {canEdit && <button className="btn btn-outline btn-sm" onClick={() => handleRestore(p)}>복원</button>}
                      </div></td>
                    </tr>
                  );
                })}
                {deletedProducts.length === 0 && <tr><td colSpan={11} style={{ textAlign:'center', padding:32, color:'#94a3b8' }}>삭제예정 상품이 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {tabBar}
      <div className="toolbar">
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ fontWeight:700, fontSize:13 }}>원가기준표:</span>
          <select className="form-input" style={{ width:220, padding:'7px 10px', fontSize:12 }} value={selectedPriceSet} onChange={e => setSelectedPriceSet(e.target.value)}>
            {priceSets.map(s => <option key={s.id} value={s.id}>{s.version} ({s.created_at?.slice(0,10)}) {s.is_active ? '★ Active' : ''}</option>)}
          </select>
          <div className="search-box"><span>🔍</span><input placeholder="상품명, 모델명, 별칭 검색..." value={search} onChange={e => setSearch(e.target.value)} /></div>
          <select className="form-input" style={{ width:130, padding:'7px 10px', fontSize:12 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">전체 유형</option>
            <option value="software">Software</option>
            <option value="license">License</option>
            <option value="hardware">Hardware</option>
            <option value="appliance">Appliance</option>
          </select>
          <select className="form-input" style={{ width:130, padding:'7px 10px', fontSize:12 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="">전체 구분</option>
            {categories.map(c => <option key={c.id} value={c.label}>{c.label}</option>)}
          </select>
          <select className="form-input" style={{ width:110, padding:'7px 10px', fontSize:12 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">전체 상태</option>
            {statuses.map(s => <option key={s.id} value={s.code}>{s.label}</option>)}
          </select>
        </div>
        {canEdit && <button className="btn btn-primary btn-sm" onClick={() => navigate('/products/new')}>✚ 상품 등록</button>}
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center' }}>로딩 중...</div> : (
          <div style={{ overflowX:'auto' }}>
            <table>
              <thead><tr>
                <th onClick={()=>toggleSort('code')}>상품코드 <SortIcon col="code" /></th>
                <th onClick={()=>toggleSort('product_type')}>유형 <SortIcon col="product_type" /></th>
                <th onClick={()=>toggleSort('category')}>상품구분 <SortIcon col="category" /></th>
                <th onClick={()=>toggleSort('name')}>상품명 <SortIcon col="name" /></th>
                <th onClick={()=>toggleSort('model')}>모델명 <SortIcon col="model" /></th>
                <th onClick={()=>toggleSort('alias')}>별칭 <SortIcon col="alias" /></th>
                <th style={{textAlign:'right'}} onClick={()=>toggleSort('consumer_price')}>소비자단가 <SortIcon col="consumer_price" /></th>
                <th style={{textAlign:'right'}} onClick={()=>toggleSort('total_cost')}>원가합계 <SortIcon col="total_cost" /></th>
                <th style={{textAlign:'center'}} onClick={()=>toggleSort('part_count')}>부품수 <SortIcon col="part_count" /></th>
                <th onClick={()=>toggleSort('status')}>상태 <SortIcon col="status" /></th>
                <th>작업</th>
              </tr></thead>
              <tbody>
                {sortedProducts.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontFamily:'monospace' }}>{p.code}</td>
                    <td>
                      <span className={`badge ${TYPE_BADGE[p.product_type]||'badge-gray'}`} title={TYPE_LABEL[p.product_type]||p.product_type}>
                        {TYPE_SHORT[p.product_type]||p.product_type||'-'}
                      </span>
                    </td>
                    <td><span className="badge badge-gray">{p.category}</span></td>
                    <td className="clickable fw-bold" onClick={() => navigate(`/products/new?id=${p.id}`)}>{p.name}</td>
                    <td>{p.model}</td>
                    <td>{p.alias || '-'}</td>
                    <td style={{ textAlign:'right' }}>₩{Number(p.consumer_price||0).toLocaleString()}</td>
                    <td style={{ textAlign:'right' }}>
                      <div style={{ fontWeight:700 }}>₩{Number(p.total_cost||0).toLocaleString()}</div>
                      {isComparing && p.compare_cost != null && (() => {
                        const activeCost = parseInt(p.total_cost||0);
                        const compareCost = parseInt(p.compare_cost);
                        const diff = activeCost - compareCost;
                        return (
                          <>
                            {diff !== 0 && <div style={{ fontSize:11, fontWeight:700, color: diff > 0 ? '#ef4444' : '#22c55e' }}>{diff > 0 ? '▲' : '▼'} ₩{Math.abs(diff).toLocaleString()}</div>}
                            {diff === 0 && <div style={{ fontSize:11, color:'#94a3b8' }}>변동 없음</div>}
                            <div style={{ fontSize:11, color:'#3b82f6' }}>{compareSetVersion}: ₩{compareCost.toLocaleString()}</div>
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign:'center' }}>{p.part_count}</td>
                    <td><span className={`badge ${statusBadge[p.status]||'badge-gray'}`}>{statuses.find(s=>s.code===p.status)?.label || p.status}</span></td>
                    <td><div className="btn-group">
                      {canEdit && <button className="btn btn-outline btn-sm" onClick={() => navigate(`/products/new?id=${p.id}`)}>편집</button>}
                      {canEdit && <button className="btn btn-outline btn-sm" onClick={() => handleClone(p.id)}>복제</button>}
                      {canEdit && <button className="btn btn-danger btn-sm" onClick={() => openDelete(p)}>삭제</button>}
                    </div></td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td colSpan={11} style={{ textAlign:'center', padding:32, color:'#94a3b8' }}>등록된 상품이 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDelete && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:420 }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0' }}>삭제 확인</div>
            <div style={{ fontSize:13, marginBottom:16 }}><strong>"{showDelete.name} ({showDelete.alias||showDelete.model})"</strong>을 삭제하시겠습니까?</div>
            <div className="form-group"><label className="form-label">비밀번호</label><input className="form-input" type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="현재 계정 비밀번호" /></div>
            {usage && usage.appliance_component_count > 0 && (
              <div className="alert alert-danger" style={{ marginBottom:12, fontSize:12 }}>이 상품은 다른 Appliance {usage.appliance_component_count}개의 구성품으로 사용 중이라 삭제할 수 없습니다.</div>
            )}
            {usage && usage.estimate_count > 0 && (
              <div className="alert alert-info" style={{ marginBottom:12, fontSize:12 }}>
                이 상품은 견적서 {usage.estimate_count}건에서 사용 중입니다. 삭제해도 견적서의 품명·모델·사양·단가 등 내용은 그대로 유지되며,
                완전 삭제 후에는 해당 견적서에서 상품의 최신 정보(가격 설정 정보 등)만 표시되지 않습니다.
              </div>
            )}
            <div className="alert alert-warning" style={{ marginBottom:16 }}>⚠ 삭제 후 7일간 복원 가능하며, 7일 후 완전히 삭제됩니다.</div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowDelete(null)}>취소</button>
              <button className="btn btn-danger" disabled={!deletePassword} style={{ opacity:deletePassword?1:0.5, background:'#ef4444', color:'#fff', border:'none' }} onClick={handleDelete}>삭제</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
